import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Database, Lock, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isPlatformAdmin, isEazyStaff } from "@/lib/roles";

// Item 24: per-tenant switch "the tenant may connect its own data sources" + the managed
// sources Eazy has provisioned into this tenant.
export function TenantSourcePolicyCard() {
  const { user } = useAuth();
  const canEdit = isPlatformAdmin(user?.role);
  const staff = isEazyStaff(user?.role) || canEdit;
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [allow, setAllow] = useState(true);
  const [sources, setSources] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (staff) api.get("/tenants").then(({ data }) => {
      setTenants(data || []);
      setTenantId((data || [])[0]?.id || "");
    }).catch(() => setTenants([]));
    else setTenantId(user?.tenant_id || "");
  }, [staff, user?.tenant_id]);

  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/source-policy`).then(({ data }) => setAllow(!!data.allow_tenant_data_sources)).catch(() => {});
  }, [tenantId]);

  useEffect(() => {
    api.get("/managed-sources").then(({ data }) => setSources(data.items || [])).catch(() => setSources([]));
  }, [tenantId]);

  const save = async (v) => {
    setBusy(true);
    try {
      await api.put(`/tenants/${tenantId}/source-policy`, { allow_tenant_data_sources: v });
      setAllow(v);
      toast.success(v ? "This tenant can add its own data sources" : "This tenant can no longer add data sources");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the policy"); }
    finally { setBusy(false); }
  };

  const setLevel = async (connId, level) => {
    try {
      await api.put(`/managed-sources/${connId}/visibility`, { level });
      setSources((s) => (s || []).map((x) => (x.id === connId ? { ...x, managed: { ...(x.managed || {}), level } } : x)));
      toast.success("Visibility updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update visibility"); }
  };

  const visible = (sources || []).filter((s) => !staff || !tenantId || s.tenant_id === tenantId);

  return (
    <div className="space-y-4" data-testid="tenant-source-policy">
      <div className="glass-card p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-base text-[color:var(--ink)] flex items-center gap-2">
              <Database className="w-4 h-4 text-[color:var(--brand-ocean)]" />Data source policy
            </h3>
            <p className="text-xs text-[color:var(--ink-3)] mt-1 max-w-xl">
              When this is off, the workspace can only use the data sources Eazy provisions for it —
              nobody inside the tenant can connect a database of their own.
            </p>
          </div>
          {staff && tenants.length > 0 && (
            <Select value={tenantId} onValueChange={setTenantId}>
              <SelectTrigger data-testid="policy-tenant" className="w-[200px] h-8 text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-center justify-between mt-4 rounded-lg bg-[color:var(--surface-2)] px-3 py-2.5">
          <span className="text-xs text-[color:var(--ink-2)]">Tenant can create their own data sources</span>
          <span className="flex items-center gap-2">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[color:var(--ink-3)]" />}
            <Switch data-testid="policy-allow-sources" checked={allow} disabled={!canEdit || busy} onCheckedChange={save}
              className={!canEdit ? "opacity-50 cursor-not-allowed" : undefined} />
          </span>
        </div>
        {!canEdit && (
          <p className="text-[11px] text-[color:var(--ink-3)] mt-2 flex items-center gap-1">
            <Lock className="w-3 h-3" />Only your Eazy administrator can change this.
          </p>
        )}
      </div>

      <div className="glass-card p-4">
        <h3 className="font-display text-base text-[color:var(--ink)] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[color:var(--brand-mint)]" />Eazy-managed sources
        </h3>
        <p className="text-xs text-[color:var(--ink-3)] mt-1">
          Provisioned by Eazy. Connection details are encrypted server-side and are never returned by any API.
        </p>
        {sources === null ? (
          <Loader2 className="w-4 h-4 animate-spin mt-3 text-[color:var(--ink-3)]" />
        ) : !visible.length ? (
          <p data-testid="managed-sources-empty" className="text-xs text-[color:var(--ink-3)] mt-3">No managed sources here yet.</p>
        ) : (
          <div className="mt-3 space-y-1.5" data-testid="managed-sources-list">
            {visible.map((s) => (
              <div key={s.id} data-testid="managed-source-row" className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--glass-border)] px-3 py-2">
                <span className="min-w-0">
                  <span className="text-xs text-[color:var(--ink)] truncate block">{s.name}</span>
                  <span className="text-[10px] text-[color:var(--ink-3)]">{s.db_type} · {s.dataset_count} dataset{s.dataset_count === 1 ? "" : "s"}{s.managed?.by ? ` · by ${s.managed.by}` : ""}</span>
                </span>
                {staff ? (
                  <Select value={s.managed?.level || "credentials"} onValueChange={(v) => setLevel(s.id, v)}>
                    <SelectTrigger data-testid={`managed-level-${s.id}`} className="w-[190px] h-7 text-[11px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credentials">Hide credentials</SelectItem>
                      <SelectItem value="definition">Hide credentials + query</SelectItem>
                      <SelectItem value="hidden">Hide the source completely</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="text-[10px] shrink-0">{s.managed?.label || "Managed by Eazy"}</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
